import React, { ChangeEvent, Suspense, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import cx from "classnames";

import { Dropdown } from "react-bootstrap";
import styles from './mediaLibrary.module.scss';
import { useDispatch } from "react-redux";
import { fetchExternalMediaLibrary, fetchMediaLibrary, fetchMediaLibraryCategories } from "../../redux/actions/mediaLibrary";
import InfiniteScroll from "../infiniteScroll";
import { useSelector } from "react-redux";
import { MediaLibrary } from "../../types/mediaLibrary";
import { animated, useTransition } from "@react-spring/web";
import AuthService from "src/utils/auth.service";

import Loading from "../loading";
import { FileService } from "src/utils/file.service";
import AssetService from "src/utils/asset.service";
import EmptyMessage from "../emptyMessage";

 import "@paperflite-marketing/asset-viewer/dist/index.css";
import 'videojs-seek-buttons/dist/videojs-seek-buttons.css';
import 'videojs-seek-buttons';
import 'video.js/dist/video-js.css';

interface VideoViewerProps {
  id: string;
  className?: string;
  src: string | null;
  mode: 'light' | 'dark';
  metadata: any; 
  active: boolean;
  icon?: string;
  poster?: string;
  videoProperties: any;
  subtitleEnabled: boolean;
  caption: string | null;
  onPlay?: () => void;
  onCaptureFrame?: (image: any) => void;
};

const VideoViewer = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.VideoViewer,
  })) as Promise<{ default: React.ComponentType<VideoViewerProps> }>
);

const SOURCES = [{
  label: 'Paperflite',
  value: 'paperflite',
  icon: '/images/common/banner/sources/paperflite.svg'
}, {
  label: 'Pexels',
  value: 'pexels',
  icon: '/images/common/banner/sources/pexels.svg'
}, {
  label: 'Pick From Video',
  value: 'pick-from-video',
  icon: ''
}, {
  label: 'Pick From Pages',
  value: 'pick-from-pages',
  icon: ''
}];

const MEDIA_TYPES = [{
  label: 'Images',
  value: 'image'
}, {
  label: 'Videos',
  value: 'video'
}];

interface MediaLibraryChooserProps {
  entityType: 'collection' | 'stream' | 'asset' | 'group' | 'course';
  // eslint-disable-next-line no-unused-vars
  onChange: (media: MediaLibrary) => void;
  handleSelectedMediaSource?: (media: string) => void;
  columns: number;
  handleCaptureFrame?: (image: string | undefined) => void;
  handlePickFromPage?: (image: string | undefined) => void;
}

const MediaLibraryChooser: React.FC<MediaLibraryChooserProps> = ({ entityType, onChange, handleSelectedMediaSource, columns, handleCaptureFrame, handlePickFromPage }) => {
  const [source, setSource] = React.useState<string | undefined>(undefined);
  const [mediaType, setMediaType] = React.useState<'video' | 'image'>(MEDIA_TYPES[0].value as 'video' | 'image');
  const [category, setCategory] = React.useState<string | null | undefined>(null);
  const [query, setQuery] = React.useState<string>("design");
  const [options, setOptions] = React.useState<any[]>([]);
  const asset = useSelector((state: any) => _.get(state, "asset.item", null));
  const preview = useSelector((state: any) => _.get(state, "asset.preview.item", null));

  const fileType = FileService.getFileType(_.get(asset, "metadata", ""));

  useEffect(() => {
    if (entityType === "stream") {
      setSource(SOURCES.find(s => s.value === 'paperflite')?.value || undefined);
      setOptions(SOURCES.filter(s => s.value === 'paperflite'));

    } else if (entityType === "group") {
      setSource(SOURCES.find(s => s.value === 'pexels')?.value || undefined);
      setOptions(SOURCES.filter(s => s.value === 'pexels'));
      
    } else if(entityType === "asset" && fileType === 'VIDEO') {
      setSource(SOURCES[0].value || undefined);
      setOptions(SOURCES.filter(s => { 
       return s.value !== 'pick-from-pages';
      })); 

    } else if(entityType === "asset" && _.get(preview, "pages", []).length > 0) {
      setSource(SOURCES[0].value || undefined);
      setOptions(SOURCES.filter(s => { 
       return s.value !== 'pick-from-video';
      })); 
      
    } else {
      setSource(SOURCES[0].value || undefined);
      setOptions(SOURCES.filter(s => { 
        return s.value !== 'pick-from-video' && s.value !== 'pick-from-pages';
       })); 
    }
  }, [entityType]);

  const categories = useSelector((state: any) => {
    const items = _.get(state, `mediaLibrary['${entityType}'].categories`);
    return (items || []).map((item) => {
      return {
        label: item.name,
        value: item.id
      };
    });
  }, _.isEqual);

  const sourceTransitions = useTransition(source, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleSourceChange = (value: string) => {
    setSource(value);
    if (handleSelectedMediaSource) {
      handleSelectedMediaSource(value);
    }
  }

  const handleMediaTypeChange = (value: string) => {
    const media = value as 'video' | 'image';
    setMediaType(media);
  }

  const handleCategoryChange = (value: string) => {
    setCategory(value);
  }

  const handleQueryChange = (value: string) => {
    setQuery(value);
  }

  const handleMediaSelect = (media) => {
    onChange(media);
  }

  return (
    <div className={styles.bannerChooser}>
      <div className={styles.header}>
        {entityType === "collection" && (<h2>Select a banner</h2>)}
        <SelectMenu selected={source} options={options} onChange={handleSourceChange} />
        {entityType !== "asset" && entityType !== "group" && 
        <SelectMenu selected={mediaType} options={MEDIA_TYPES} onChange={handleMediaTypeChange} />
        }
        <div className={styles.filters}>
          {sourceTransitions((style, item) => (
            <animated.div className={styles.inner} style={style}>
              {item === 'paperflite' && entityType === "collection" &&
                <SelectMenu selected={category} placeholder="All Categories" options={categories} onChange={handleCategoryChange} />
              }
              
              {item === 'pexels' &&
                <QueryField value={query} onChange={handleQueryChange} />
              }
            </animated.div>
          ))}
        </div>
      </div>

      <div className={styles.content}>
        {sourceTransitions((style, item) => (
          <animated.div className={styles.inner} style={style}>
            {item === 'paperflite' &&
              <PaperfliteMediaLibrary entityType={entityType} mediaType={mediaType} category={category} onChange={handleMediaSelect} columns={columns}/>
            }

            {item === 'pexels' &&
              <PexelsMediaLibrary entityType={entityType} mediaType={mediaType} query={query} onChange={handleMediaSelect} columns={columns}/>
            }

            {item === 'pick-from-video' &&
              <PickFromVideo handleCaptureFrame={handleCaptureFrame}/>
            }

            {item === 'pick-from-pages' && handlePickFromPage &&
              <PickFromPages handlePickFromPage={handlePickFromPage} />
            }
          </animated.div>
        ))}
      </div>
    </div>
  );
}

interface PaperfliteMediaLibraryProps {
  entityType: 'collection' | 'stream' | 'asset' | 'group' | 'course';
  mediaType: 'video' | 'image';
  category?: string | null | undefined;
  // eslint-disable-next-line no-unused-vars
  onChange: (media: MediaLibrary) => void;
  columns: number;
}

const PaperfliteMediaLibrary: React.FC<PaperfliteMediaLibraryProps> = ({
  entityType, mediaType, category, onChange, columns
}) => {
  const dispatch = useDispatch();

  const { mediaItems, loading, hasMoreItems, nextPage } = useSelector((state: any) => {
    const mediaLibrary = _.get(state, `mediaLibrary['${entityType}']`);
    const mediaItems = _.get(mediaLibrary, 'items') || [];
    const loading = _.get(mediaLibrary, 'loading');
    const pagination = _.get(mediaLibrary, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage != null && nextPage >= total) {
      nextPage = null;
    }

    return {
      mediaItems,
      loading,
      hasMoreItems,
      nextPage
    };
  }, _.isEqual);

  useEffect(() => {
    dispatch(fetchMediaLibraryCategories({ entityType: entityType }));
  }, [entityType]);

  useEffect(() => {
    fetchMediaLibraryForPage(0);
  }, [entityType, mediaType, category]);

  const fetchMediaLibraryForPage = (page) => {
    dispatch(fetchMediaLibrary({
      entityType: entityType,
      mediaType: mediaType,
      category: category,
      page: page,
      limit: 40,
    }));
  }

  const handleLoadMoreItems = () => {
    if (hasMoreItems && nextPage) {
      fetchMediaLibraryForPage(nextPage);
    }
  }

  const handleCardClick = (media) => {
    onChange(media);
  }

  return (
    <InfiniteScroll className={styles.grid}
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(100px, 1fr))` }}
      loading={loading}
      hasMore={hasMoreItems}
      loadMore={handleLoadMoreItems}
      loader={() => {
        return _.times(_.size(mediaItems) == 0 ? 4 : 1).map((i) => (
          <PlaceholderMediaCard key={i} entityType={entityType} />
        ));
      }}
    >
      {mediaItems.map((mediaItem: MediaLibrary, index: number) => {
        return (
          <MediaCard key={index} mediaItem={mediaItem} entityType={entityType} onClick={handleCardClick} />
        );
      })}
    </InfiniteScroll>
  );
}

interface PexelsMediaLibraryProps {
  entityType: 'collection' | 'stream' | 'asset' | 'group' | 'course';
  mediaType: 'video' | 'image';
  query?: string | null | undefined;
  // eslint-disable-next-line no-unused-vars
  onChange: (media: MediaLibrary) => void;
  columns: number;
}

const PexelsMediaLibrary: React.FC<PexelsMediaLibraryProps> = ({
  entityType, mediaType, query, onChange, columns
}) => {
  const dispatch = useDispatch();

  const { mediaItems, loading, hasMoreItems, nextPage } = useSelector((state: any) => {
    const mediaLibrary = _.get(state, `mediaLibrary['pexels']`);
    const mediaItems = _.get(mediaLibrary, 'items') || [];
    const loading = _.get(mediaLibrary, 'loading');
    const pagination = _.get(mediaLibrary, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage != null && nextPage >= total) {
      nextPage = null;
    }

    return {
      mediaItems,
      loading,
      hasMoreItems,
      nextPage
    };
  }, _.isEqual);

  useEffect(() => {
    fetchExternalMediaLibraryForPage(0);
  }, [mediaType, query]);

  const fetchExternalMediaLibraryForPage = (page) => {
    const formattedQuery = query ? query.replace(/\s+/g, '%20') : '';
    dispatch(fetchExternalMediaLibrary({
      mediaType: mediaType,
      mediaLibraryType: 'pexels',
      orientation: 'landscape',
      query: formattedQuery,
      page: page,
      limit: 40,
    }));
  }

  const handleLoadMoreItems = () => {
    if (hasMoreItems && nextPage) {
      fetchExternalMediaLibraryForPage(nextPage);
    }
  }

  const handleCardClick = (media) => {
    onChange(media);
  }

  const empty = !loading && _.size(mediaItems) === 0;

  if (empty) {
    return (
      <div className={styles.emptyContainer}>
      <EmptyMessage
        width={450}
        image={'/images/common/banner/sources/pexel-empty-search.svg'}
        message={"Don't panic, nothing's broken, just couldn't find what you are looking for, try using a different term."}
      />
    </div>
    );
  }

  return (
    <InfiniteScroll className={styles.grid}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(100px, 1fr))` }}
      loading={loading}
      hasMore={hasMoreItems}
      loadMore={handleLoadMoreItems}
      loader={() => {
        return _.times(_.size(mediaItems) == 0 ? 4 : 1).map((i) => (
          <PlaceholderMediaCard key={i} entityType={entityType} />
        ));
      }}
    >
      {mediaItems.map((mediaItem: MediaLibrary, index: number) => {
        return (
          <MediaCard key={index} mediaItem={mediaItem} entityType={entityType} onClick={handleCardClick} />
        );
      })}
    </InfiniteScroll>
  );
}

interface MediaCardProps {
  mediaItem: MediaLibrary;
  entityType: 'collection' | 'stream' | 'asset' | 'group' | 'course' | 'settings';
  // eslint-disable-next-line no-unused-vars
  onClick?: (mediaItem: any) => void;
}

const MediaCard: React.FC<MediaCardProps> = ({ mediaItem, entityType, onClick }) => {
  const imageRef = React.useRef<HTMLImageElement>(null);
  const thumbnailUrl = mediaItem.thumbnailUrl;

  const handleClick = () => {
    if (onClick) {
      const width = imageRef.current?.naturalWidth;
      const height = imageRef.current?.naturalHeight;

      const media = {
        ...mediaItem,
        width: width,
        height: height
      };

      onClick(media);
    }
  }

  return (
    <div className={cx(styles.mediaItem, styles[entityType])} onClick={handleClick}>
      <img ref={imageRef} 
        src={thumbnailUrl} 
        className={styles.icon} 
        alt={mediaItem.id} 
      />
    </div>
  );
}

const PlaceholderMediaCard: React.FC<{ entityType: 'collection' | 'stream' | 'asset' | 'group' | 'course' }> = ({ entityType }) => {
  return (
    <div className={cx(styles.mediaItem, styles.placeholder, styles[entityType])}>
      <div className={cx(styles.icon, styles.block)}></div>
    </div>
  );
}

//Query Field
interface QueryFieldProps {
  value: string;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: string) => void;
}

const QueryField: React.FC<QueryFieldProps> = ({ value, onChange }) => {
  const [query, setQuery] = useState<string>(value);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onChange(query);
  }

  useEffect(() => {
    setQuery(value);
  }, [value])

  return (
    <form className={styles.queryForm} onSubmit={handleSubmit}>
      <input className={styles.query} value={query} placeholder="Search in pexels" onChange={handleChange} />
      <button type="submit">
        <i className="fa-solid fa-magnifying-glass"></i>
      </button>
    </form>
  );
}

interface SelectMenuProps {
  selected?: string | null;
  placeholder?: string;
  options: {
    icon?: string;
    label: string;
    value: string;
  }[];
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: string) => void;
}

const SelectMenu: React.FC<SelectMenuProps> = ({ selected, placeholder, options, onChange }) => {
  const selectedOption = useMemo(() => {
    return options.find(option => option.value === selected);
  }, [selected, options]);

  const handleSelect = (value: string) => {
    if (onChange) {
      onChange(value);
    }
  }

  return (
    <Dropdown className={styles.selectDropdown}>
      <Dropdown.Toggle>
        {selectedOption && (
          <>
            {selectedOption?.icon && (
              <img src={selectedOption.icon} alt={selectedOption.label} />
            )}
            {selectedOption?.label}
          </>
        )}

        {!selectedOption && placeholder}
        {_.size(options) > 1 && <i className="paperflite-dropdown"></i>}
      </Dropdown.Toggle>

      {_.size(options) > 1 && (
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
            ],
          }}
        >
          {options.map((option, index) => {
            const selected = option.value === selectedOption?.value;
            return (
              <Dropdown.Item
                key={index}
                className={cx({ [styles.active]: selected })}
                onClick={() => handleSelect(option.value)}
              >
                {selected ? (
                  <i className="fa-solid fa-circle-check"></i>
                ) : (
                  <i className="fa-regular fa-circle"></i>
                )}
                <span>{option.label}</span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );

}

interface PickFromVideoProps {
  handleCaptureFrame?: (image: string | undefined) => void;
}

const PickFromVideo: React.FC<PickFromVideoProps> = ({handleCaptureFrame }) => {

  const asset = useSelector((state: any) => _.get(state, "asset.item", null));
  const loading = useSelector((state: any) => _.get(state, "asset.loading", true));

  const subtitleEnabled = useSelector((state) => _.get(state, "account.item.subtitleEnabled", true));
  const caption = _.get(asset, "viewer.subtitle", null)

  if(loading) {
    return <Loading />
  }

  let videoUrl = AuthService.getURL(
    `api/2.0/assets/${asset.id}/download`,{
      modifiedDate: asset.metadata.modified_date,
      preview: true,
    },
  );
 
  var iconUrl = _.get(asset, "icon.full");
  var poster = _.get(asset, "poster");
  
  let videoProperties = null;

  return (
    <div className={styles.pickVideoContainer}>
      <Suspense fallback={<div className={styles.videoViewer}></div>}>
        <VideoViewer
          id={`asset_viewer_${asset.id}`}
          className={cx(styles.videoViewer)}
          mode="light"
          src={videoUrl}
          videoProperties={videoProperties}
          metadata={asset.metadata}
          active={true}
          icon={iconUrl}
          poster={poster}
          onPlay={() => {
            console.log("Video started playing");
          }}
          subtitleEnabled={subtitleEnabled}
          caption={caption}
          onCaptureFrame={(image) =>
            handleCaptureFrame && handleCaptureFrame(image)
          }
        />
      </Suspense>
    </div>
  );
}

interface PickFromPagesProps {
  handlePickFromPage: (image: string | undefined) => void;
}

const PickFromPages: React.FC<PickFromPagesProps> = ({handlePickFromPage}) => {
  const assetPreviewPages = useSelector((state: any) => _.get(state, "asset.preview.item.pages", []));

  const columns = 3;

  return (
    <InfiniteScroll
      className={styles.grid}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(100px, 1fr))` }}
      loading={false}
      hasMore={false}
      loadMore={() => {}}
    >
      {(assetPreviewPages || []).map((item) => {
        return (
          <div className={styles.pageCard} onClick={() => handlePickFromPage(item.background)}>
            <div className={styles.overlay} />
            <img
              className={styles.preview}
              src={item.background.full}
            />
          </div>
        );
      })}
    </InfiniteScroll>
  ); 
}

export { MediaLibraryChooser };